import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MessageSquare, Search, Mail, Send, Eye, CheckCircle, Clock, XCircle, Plus, Reply } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ContactsTab({ token }) {
  const [contactMessages, setContactMessages] = useState([]);
  const [portalMessages, setPortalMessages] = useState([]);
  const [activeSubTab, setActiveSubTab] = useState('portal');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyForm, setReplyForm] = useState({ subject: '', message: '' });
  const [sending, setSending] = useState(false);
  
  // New message compose state
  const [showCompose, setShowCompose] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [composeForm, setComposeForm] = useState({ recipient_id: '', subject: '', message: '' });
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    // Fetch contact form messages
    axios.get(`${API}/admin/contacts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setContactMessages(res.data)).catch(console.error);
    
    // Fetch portal messages
    axios.get(`${API}/admin/portal-messages`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setPortalMessages(res.data)).catch(console.error);
  }, [token]);

  // Fetch users when compose is opened
  useEffect(() => {
    if (showCompose) {
      setLoadingUsers(true);
      const params = new URLSearchParams();
      if (selectedRole && selectedRole !== 'all') params.append('role', selectedRole);
      if (userSearch) params.append('search', userSearch);
      
      axios.get(`${API}/admin/users-for-messaging?${params.toString()}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      })
        .then(res => setAllUsers(res.data))
        .catch(console.error)
        .finally(() => setLoadingUsers(false));
    }
  }, [showCompose, selectedRole, userSearch, token]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyForm.subject.trim() || !replyForm.message.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/admin/portal-messages/reply`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          original_message_id: selectedMessage.id,
          subject: replyForm.subject,
          message: replyForm.message
        }
      });
      toast.success('Reply sent successfully');
      setReplyForm({ subject: '', message: '' });
      setSelectedMessage(null);
      // Refresh messages
      const res = await axios.get(`${API}/admin/portal-messages`, { headers: { Authorization: `Bearer ${token}` } });
      setPortalMessages(res.data);
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleSendNewMessage = async (e) => {
    e.preventDefault();
    if (!composeForm.recipient_id || !composeForm.subject.trim() || !composeForm.message.trim()) {
      toast.error('Please select a recipient and fill in subject and message');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/admin/portal-messages/send`, composeForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message sent successfully');
      setComposeForm({ recipient_id: '', subject: '', message: '' });
      setShowCompose(false);
      // Refresh messages
      const res = await axios.get(`${API}/admin/portal-messages`, { headers: { Authorization: `Bearer ${token}` } });
      setPortalMessages(res.data);
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-contacts-tab">
      {/* Sub-tabs and New Message Button */}
      <div className="flex gap-2 mb-4 justify-between items-center">
        <div className="flex gap-2">
          <Button 
            variant={activeSubTab === 'portal' ? 'default' : 'outline'}
            onClick={() => setActiveSubTab('portal')}
            className={`rounded-sm ${activeSubTab === 'portal' ? 'bg-[#1B2A4A]' : ''}`}
          >
            <MessageSquare className="h-4 w-4 mr-1" /> Portal Messages ({portalMessages.length})
          </Button>
          <Button 
            variant={activeSubTab === 'contact' ? 'default' : 'outline'}
            onClick={() => setActiveSubTab('contact')}
            className={`rounded-sm ${activeSubTab === 'contact' ? 'bg-[#1B2A4A]' : ''}`}
          >
            <Mail className="h-4 w-4 mr-1" /> Contact Form ({contactMessages.length})
          </Button>
        </div>
        <Button 
          onClick={() => setShowCompose(true)}
          className="bg-[#C5A572] text-white rounded-sm"
          data-testid="admin-new-message-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> New Message
        </Button>
      </div>

      {/* Compose New Message Modal */}
      <Dialog open={showCompose} onOpenChange={setShowCompose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A] flex items-center gap-2">
              <Send className="h-5 w-5" /> Send New Message
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSendNewMessage} className="space-y-4">
            {/* Role Filter */}
            <div>
              <Label className="text-sm">Filter by Role</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="mt-1 rounded-sm">
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="candidate">Candidate</SelectItem>
                  <SelectItem value="examiner">Examiner</SelectItem>
                  <SelectItem value="institutional">Institutional</SelectItem>
                  <SelectItem value="curriculum">Curriculum</SelectItem>
                  <SelectItem value="examination_center">Examination Center</SelectItem>
                  <SelectItem value="accreditation">Accreditation</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* User Search */}
            <div>
              <Label className="text-sm">Search User</Label>
              <Input
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="mt-1 rounded-sm"
              />
            </div>

            {/* User Selection */}
            <div>
              <Label className="text-sm">Select Recipient *</Label>
              <Select 
                value={composeForm.recipient_id} 
                onValueChange={(v) => setComposeForm({...composeForm, recipient_id: v})}
              >
                <SelectTrigger className="mt-1 rounded-sm">
                  <SelectValue placeholder={loadingUsers ? "Loading users..." : "Select a user"} />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {allUsers.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.full_name} ({user.email}) - {user.role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Subject */}
            <div>
              <Label className="text-sm">Subject *</Label>
              <Input
                value={composeForm.subject}
                onChange={(e) => setComposeForm({...composeForm, subject: e.target.value})}
                placeholder="Enter subject..."
                className="mt-1 rounded-sm"
                required
              />
            </div>

            {/* Message */}
            <div>
              <Label className="text-sm">Message *</Label>
              <Textarea
                value={composeForm.message}
                onChange={(e) => setComposeForm({...composeForm, message: e.target.value})}
                rows={5}
                placeholder="Type your message..."
                className="mt-1 rounded-sm"
                required
              />
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => setShowCompose(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" disabled={sending} className="bg-[#1B2A4A] text-white rounded-sm">
                <Send className="h-4 w-4 mr-1" /> {sending ? 'Sending...' : 'Send Message'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {activeSubTab === 'portal' && (
        <div className="space-y-3">
          {portalMessages.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">No portal messages yet.</p>
          ) : (
            portalMessages.map((m) => (
              <Card key={m.id} className={`imcac-card ${selectedMessage?.id === m.id ? 'border-2 border-[#C5A572]' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        m.direction === 'incoming' ? 'bg-blue-500' : 'bg-green-500'
                      }`}>
                        {m.direction === 'incoming' ? (
                          <MessageSquare className="h-4 w-4 text-white" />
                        ) : (
                          <Send className="h-4 w-4 text-white" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#1B2A4A]">{m.subject}</p>
                        <p className="text-xs text-slate-500">
                          {m.direction === 'incoming' ? `From: ${m.user_name} (${m.user_email})` : `To: ${m.user_name} (${m.user_email})`}
                        </p>
                        <p className="text-xs text-slate-400">Portal: {m.portal_type} • {new Date(m.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`rounded-sm text-xs ${
                        m.status === 'replied' ? 'bg-green-100 text-green-700' :
                        m.status === 'unread' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {m.direction === 'outgoing' ? 'sent' : m.status}
                      </Badge>
                      {m.direction === 'incoming' && m.status !== 'replied' && (
                        <Button 
                          size="sm" 
                          onClick={() => {
                            setSelectedMessage(m);
                            setReplyForm({ subject: `Re: ${m.subject}`, message: '' });
                          }}
                          className="bg-[#C5A572] text-white rounded-sm text-xs"
                        >
                          <Reply className="h-3 w-3 mr-1" /> Reply
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mt-3 leading-relaxed bg-slate-50 p-3 rounded-sm">{m.message}</p>
                  
                  {/* Reply Form */}
                  {selectedMessage?.id === m.id && (
                    <form onSubmit={handleReply} className="mt-4 pt-4 border-t border-slate-200 space-y-3">
                      <div>
                        <Label className="text-sm">Reply Subject</Label>
                        <Input
                          value={replyForm.subject}
                          onChange={(e) => setReplyForm({...replyForm, subject: e.target.value})}
                          className="mt-1 rounded-sm"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Reply Message</Label>
                        <Textarea
                          value={replyForm.message}
                          onChange={(e) => setReplyForm({...replyForm, message: e.target.value})}
                          rows={4}
                          className="mt-1 rounded-sm"
                          required
                          placeholder="Type your reply..."
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" disabled={sending} className="bg-[#1B2A4A] text-white rounded-sm">
                          <Send className="h-4 w-4 mr-1" /> {sending ? 'Sending...' : 'Send Reply'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setSelectedMessage(null)} className="rounded-sm">
                          Cancel
                        </Button>
                      </div>
                    </form>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {activeSubTab === 'contact' && (
        <div className="space-y-3">
          {contactMessages.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">No contact form messages yet.</p>
          ) : (
            contactMessages.map((m) => (
              <Card key={m.id} className="imcac-card">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-[#1B2A4A]">{m.subject}</p>
                      <p className="text-xs text-slate-500">{m.name} &middot; {m.email}</p>
                    </div>
                    <span className="text-xs text-slate-400">{new Date(m.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{m.message}</p>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default ContactsTab;
